import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { useAuthStore } from '../../store/auth'

interface ToastItem {
  id: number
  message: string
  detail?: string
  type: 'success' | 'error' | 'info'
}

interface ToastContextValue {
  toast: (message: string, type?: ToastItem['type']) => void
  toastError: (message: string, error?: unknown) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {}, toastError: () => {} })

export function useToast() {
  return useContext(ToastContext)
}

// Global toast function for use outside React components (e.g. api.ts)
let globalToast: ToastContextValue['toast'] | null = null
let globalToastError: ToastContextValue['toastError'] | null = null
export function getGlobalToast() { return globalToast }
export function getGlobalToastError() { return globalToastError }

let nextId = 0

function isAdminRole(role?: string): boolean {
  return role === 'super_admin' || role === 'admin'
}

function extractErrorDetail(error: unknown): string {
  if (!error) return ''
  if (error instanceof Error) {
    const parts = [error.message]
    if (error.stack) parts.push(error.stack)
    if ((error as unknown as Record<string, unknown>).status) parts.push(`Status: ${(error as unknown as Record<string, unknown>).status}`)
    return parts.join('\n')
  }
  if (typeof error === 'string') return error
  try { return JSON.stringify(error, null, 2) } catch { return String(error) }
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const userRole = useAuthStore(s => s.user?.role)

  const toast = useCallback((message: string, type: ToastItem['type'] = 'success') => {
    const id = nextId++
    setToasts(prev => [...prev, { id, message, type }])
    const timeout = type === 'error' ? 6000 : 3000
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), timeout)
  }, [])

  const toastError = useCallback((message: string, error?: unknown) => {
    const id = nextId++
    const detail = isAdminRole(userRole) ? extractErrorDetail(error) : undefined
    setToasts(prev => [...prev, { id, message, type: 'error', detail }])
    const timeout = detail ? 10000 : 6000
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), timeout)
  }, [userRole])

  // Register global accessors
  useEffect(() => {
    globalToast = toast
    globalToastError = toastError
    return () => { globalToast = null; globalToastError = null }
  }, [toast, toastError])

  return (
    <ToastContext.Provider value={{ toast, toastError }}>
      {children}
      <div style={{
        position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <DynamicToastItem
            key={t.id}
            item={t}
            onDismiss={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const ICON_COLORS: Record<string, string> = {
  success: 'var(--ok)',
  error: 'var(--err)',
  info: 'var(--info)',
}

function CheckIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  )
}

const ICONS: Record<string, () => React.ReactElement> = {
  success: CheckIcon,
  error: XIcon,
  info: InfoIcon,
}

function DynamicToastItem({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const iconColor = ICON_COLORS[item.type] || 'var(--info)'
  const Icon = ICONS[item.type] || InfoIcon

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true))
  }, [])

  return (
    <div
      onClick={item.detail ? undefined : onDismiss}
      style={{
        pointerEvents: 'auto',
        background: 'rgba(18,18,24,.9)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--border-h)',
        borderRadius: 'var(--r-f)',
        padding: item.detail ? '8px 16px 8px 12px' : '8px 20px 8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: 'var(--sh-lg)',
        fontFamily: 'var(--font)',
        fontSize: 13,
        color: 'var(--text-1)',
        cursor: 'pointer',
        transform: visible ? 'translateY(0)' : 'translateY(-20px)',
        opacity: visible ? 1 : 0,
        transition: `all var(--dur-s) ${visible ? 'var(--ease-sp)' : 'var(--ease)'}`,
        maxWidth: item.detail ? 480 : 360,
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: `color-mix(in srgb, ${iconColor} 12%, transparent)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}
      >
        <Icon />
      </div>
      <span style={{ flex: 1 }}>{item.message}</span>
      {item.detail && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
          style={{
            background: 'none', border: 'none', color: iconColor, cursor: 'pointer',
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'underline',
            fontFamily: 'var(--font)',
          }}
        >
          {expanded ? 'Hide' : 'Details'}
        </button>
      )}
      {item.detail && expanded && (
        <pre
          onClick={(e) => {
            e.stopPropagation()
            navigator.clipboard?.writeText(item.detail || '')
          }}
          title="Click to copy"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            padding: 10, borderRadius: 'var(--r-m)', fontSize: 10,
            background: 'rgba(18,18,24,.95)', border: '1px solid var(--border-h)',
            color: 'var(--text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            maxHeight: 200, overflow: 'auto', cursor: 'copy',
            backdropFilter: 'blur(20px)',
          }}
        >
          {item.detail}
        </pre>
      )}
    </div>
  )
}
